/**
 * BookExpertDialog - book a paid call with a Quixo expert (track T10).
 *
 * Props:
 *   jobId?, findingId?   what the call is about (sent with the booking; the server checks they are this workspace's)
 *   defaultTopic?        pre-fills the topic (e.g. the finding title)
 *   onClose              close the dialog (Escape and the close button call it)
 *   onCheckout?          receives the billing checkout handoff { kind, ref, priceId, amountMinor, currency }. Billing owns
 *                        the payment page (integration request); without it the dialog shows the "awaiting payment" state.
 *   initialSlots?        preloaded open slots (SSR / tests); skips the availability fetch
 *   api?                 injected client (tests)
 *
 * The booking is created in `payment_pending` with an Idempotency-Key (a double click never books twice). Times are
 * shown in Dubai time; the customer picks 1-5 preferred times and a Quixo expert confirms one.
 */
import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Button, ErrorState, Loading } from '@qm/ui';
import { useApp } from '../../../../../lib/app-context';
import {
  advisorApi, bookingErrorKey, bookingProblems, groupSlotsByDay, MAX_SLOTS, newIdempotencyKey, TOPIC_MAX, toggleSlot,
  type AdvisorApi, type Booking, type CheckoutHandoff,
} from './api';
import './advisor.css';

export interface BookExpertDialogProps {
  jobId?: string;
  findingId?: string;
  defaultTopic?: string;
  onClose: () => void;
  onCheckout?: (c: CheckoutHandoff, booking: Booking) => void;
  initialSlots?: string[];
  api?: AdvisorApi;
}

export function BookExpertDialog({ jobId, findingId, defaultTopic = '', onClose, onCheckout, initialSlots, api }: BookExpertDialogProps) {
  const { t, locale } = useApp();
  const client = useMemo(() => api ?? advisorApi(), [api]);
  const id = useId();
  const [duration, setDuration] = useState<30 | 60>(30);
  const [slots, setSlots] = useState<string[] | null>(initialSlots ?? null);
  const [picked, setPicked] = useState<string[]>([]);
  const [topic, setTopic] = useState(defaultTopic);
  const [problems, setProblems] = useState<Partial<Record<'topic' | 'slots', string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState<Booking | null>(null);
  const key = useRef(newIdempotencyKey());
  const first = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { first.current?.focus(); }, []);
  useEffect(() => {
    if (initialSlots && duration === 30) return;
    let live = true;
    setSlots(null);
    client.availability(duration).then((r) => { if (live) setSlots(r.slots); }, (e: unknown) => { if (live) { setSlots([]); setError(bookingErrorKey(e)); } });
    return () => { live = false; };
  }, [client, duration, initialSlots]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const p = bookingProblems({ topic, slots: picked });
    setProblems(p);
    if (p.topic || p.slots) return;
    setPending(true);
    setError(null);
    try {
      const r = await client.book({ ...(jobId ? { jobId } : {}), ...(findingId ? { findingId } : {}), topic: topic.trim(), durationMin: duration, preferredSlots: picked }, key.current);
      setDone(r.booking);
      if (r.checkout) onCheckout?.(r.checkout, r.booking);
    } catch (err) {
      setError(bookingErrorKey(err));
    } finally {
      setPending(false);
    }
  }

  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
  const groups = slots ? groupSlotsByDay(slots, locale) : [];

  return (
    <div className="qm-book" role="dialog" aria-modal="true" aria-labelledby={`${id}-t`} onKeyDown={onKey} data-testid="book-expert">
      <div className="qm-book__sheet">
        <div className="qm-book__head">
          <h2 id={`${id}-t`} className="qm-book__title">{t('audit-remediation.expert.book.title')}</h2>
          <Button variant="ghost" onClick={onClose} aria-label={t('audit-remediation.expert.book.close')}>{t('audit-remediation.expert.book.closeShort')}</Button>
        </div>
        {done ? (
          <div className="qm-book__done" role="status">
            <p>{t('audit-remediation.expert.book.created')}</p>
            <p className="qm-book__ref">{t('audit-remediation.expert.book.reference', { ref: done.id })}</p>
            <Button variant="primary" onClick={onClose}>{t('audit-remediation.expert.book.doneClose')}</Button>
          </div>
        ) : (
          <form className="qm-book__form" onSubmit={submit} noValidate>
            <p className="qm-book__intro">{t('audit-remediation.expert.book.intro')}</p>
            <div className="qm-field" data-invalid={problems.topic ? 'true' : undefined}>
              <label className="qm-field__label" htmlFor={`${id}-topic`}>{t('audit-remediation.expert.book.topic')}</label>
              <textarea ref={first} id={`${id}-topic`} name="topic" className="qm-input" rows={3} maxLength={TOPIC_MAX} value={topic} onChange={(e) => setTopic(e.target.value)}
                aria-describedby={problems.topic ? `${id}-topic-err` : undefined} aria-invalid={problems.topic ? true : undefined} />
              {problems.topic ? <p className="qm-field__error" id={`${id}-topic-err`} role="alert">{t(problems.topic)}</p> : null}
            </div>
            <fieldset className="qm-book__fieldset">
              <legend className="qm-field__label">{t('audit-remediation.expert.book.duration')}</legend>
              {([30, 60] as const).map((d) => (
                <label key={d} className="qm-book__radio">
                  <input type="radio" name="duration" value={d} checked={duration === d} onChange={() => { setDuration(d); setPicked([]); }} />
                  {t('audit-remediation.expert.book.minutes', { n: d })}
                </label>
              ))}
            </fieldset>
            <fieldset className="qm-book__fieldset" aria-describedby={`${id}-slots-hint${problems.slots ? ` ${id}-slots-err` : ''}`}>
              <legend className="qm-field__label">{t('audit-remediation.expert.book.slots')}</legend>
              <p className="qm-field__hint" id={`${id}-slots-hint`}>{t('audit-remediation.expert.book.slotsHint', { max: MAX_SLOTS })}</p>
              {!slots ? <Loading label={t('audit-remediation.expert.book.loadingSlots')} /> : null}
              {slots && !slots.length ? <p>{t('audit-remediation.expert.book.noSlots')}</p> : null}
              {groups.map((g) => (
                <div key={g.day} className="qm-book__day">
                  <p className="qm-book__dayLabel">{g.label}</p>
                  <div className="qm-book__times">
                    {g.slots.map((s) => (
                      <label key={s.iso} className="qm-book__time" data-checked={picked.includes(s.iso) ? 'true' : undefined}>
                        <input type="checkbox" name="slot" value={s.iso} checked={picked.includes(s.iso)} onChange={() => setPicked((cur) => toggleSlot(cur, s.iso))}
                          disabled={!picked.includes(s.iso) && picked.length >= MAX_SLOTS} />
                        <span dir="ltr">{s.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {problems.slots ? <p className="qm-field__error" id={`${id}-slots-err`} role="alert">{t(problems.slots)}</p> : null}
            </fieldset>
            <p className="qm-book__paid">{t('audit-remediation.expert.book.paidNote')}</p>
            {error ? <ErrorState message={t(error)} /> : null}
            <div className="qm-book__submit">
              <Button type="submit" variant="primary" disabled={pending}>{pending ? t('audit-remediation.expert.book.sending') : t('audit-remediation.expert.book.submit')}</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
